import { useMemo } from "react"

import { chartBarSeriesColors } from "@/components/charts/seriesPalette"
import type { ChartBarFilter } from "@/types/ChartBarFilter"
import type { RunMetricOutput } from "@/types/RunMetricOutput"
import { formatMetricDisplayName } from "@/utils/formatting/format-metric-display-results"

import {
  HorizontalStackedBarChart,
  type HorizontalBarSegment,
} from "../charts/HorizontalStackedBarChart"

// Top-N string values, colored by series identity from the shared palette.
const MAX_SET_VALUES = 4

export const AggregateOverviewSet = ({
  mos,
  onBarClick,
  activeFilters,
  includeEmptyValues = false,
  preserveRawValues = false,
}: {
  mos: RunMetricOutput[]
  onBarClick?: (filter: { type: "exact"; value: string }) => void
  activeFilters?: ChartBarFilter[]
  includeEmptyValues?: boolean
  preserveRawValues?: boolean
}) => {
  const segments = useMemo((): HorizontalBarSegment[] => {
    const count = mos.filter((m) => m.set_value !== null).length
    if (count === 0) {
      return []
    }

    // Get counts of each string value within the sets
    const valueCounts = mos.reduce((acc: Map<string, number>, curr) => {
      if (curr.set_value && Array.isArray(curr.set_value)) {
        // Count each individual string in the set_value array
        curr.set_value.forEach((value) => {
          if (value || includeEmptyValues) {
            acc.set(value, (acc.get(value) ?? 0) + 1)
          }
        })
      }
      return acc
    }, new Map<string, number>())

    // Sort alphabetically by string value and take the top N
    const topValues = [...valueCounts.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(0, MAX_SET_VALUES)

    const colors = chartBarSeriesColors(topValues.length)
    return topValues.map(([value, count], i) => ({
      label: preserveRawValues
        ? JSON.stringify(value)
        : value === ""
          ? "(empty string)"
          : formatMetricDisplayName(value),
      rawValue: value,
      value: count,
      color: colors[i],
    }))
  }, [mos, includeEmptyValues, preserveRawValues])

  if (segments.length === 0) {
    return null
  }

  return (
    <div data-dd-privacy="mask" data-ph-capture="false">
      <HorizontalStackedBarChart
        segments={segments}
        onBarClick={onBarClick}
        activeFilters={activeFilters}
      />
    </div>
  )
}
